// useRoles — the portal's single source of truth for "which roles exist".
//
// Roles stopped being a Postgres enum in migration 6048 (#612): they are rows
// in staff.roles, editable from /admin/settings with no deploy. Every surface
// that used to carry its own copy of the enum — RolePicker's ROLE_VALUES,
// useUserRoles' AppRole union + ROLE_SURFACES map, the notification-settings
// recipient checkboxes, db-enums.generated.ts's STAFF_APP_ROLE — now reads the
// list from here instead.
//
// staff.roles is readable by every authenticated user (RLS policy
// roles_select_authenticated): the rows are names, labels, chip colours and the
// portal navigation map, with no grant information in them. Capability grants
// stay admin-only and live in staff.role_capabilities.
//
// The fetch is cached module-side so the many components that need role
// metadata share one round-trip per page load rather than one each.

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface RoleMeta {
  name: string;
  label: string;
  description: string | null;
  color: string;
  /** URL prefixes this role may reach. "*" means unrestricted. */
  surfaces: string[];
  is_system: boolean;
  sort_order: number;
  retired_at: string | null;
}

export interface UseRolesResult {
  /** Active (non-retired) roles, in display order. */
  roles: RoleMeta[];
  /** Every role including retired ones — for rendering historical grants. */
  allRoles: RoleMeta[];
  loading: boolean;
  error: string | null;
}

type Listener = () => void;

let cache: RoleMeta[] | null = null;
let cacheError: string | null = null;
let inFlight: Promise<void> | null = null;
const listeners = new Set<Listener>();

function notify() {
  for (const fn of listeners) fn();
}

async function load(): Promise<void> {
  const { data, error } = await supabase
    .schema("staff")
    .from("roles")
    .select("name,label,description,color,surfaces,is_system,sort_order,retired_at")
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });

  if (error) {
    cacheError = error.message;
    cache = [];
  } else {
    cacheError = null;
    cache = ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
      name: String(r.name ?? ""),
      label: String(r.label ?? r.name ?? ""),
      description: (r.description as string | null) ?? null,
      color: String(r.color ?? "slate"),
      surfaces: Array.isArray(r.surfaces) ? (r.surfaces as string[]) : [],
      is_system: Boolean(r.is_system),
      sort_order: Number(r.sort_order ?? 100),
      retired_at: (r.retired_at as string | null) ?? null,
    }));
  }
  notify();
}

function ensureLoaded(): Promise<void> {
  if (cache !== null) return Promise.resolve();
  if (!inFlight) {
    inFlight = load().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/**
 * Drop the module-level cache so the next consumer refetches. Call this after
 * mutating staff.roles (the /admin/settings Roles panel does) so pickers and
 * chips pick the change up without a page reload.
 */
export function invalidateRolesCache(): void {
  cache = null;
  cacheError = null;
  notify();
  void ensureLoaded();
}

/**
 * Synchronous lookup against the cached registry, for the handful of plain
 * (non-hook) helpers that need a role's label or chip colour — RolePicker's
 * roleLabel()/roleChipClasses(). Returns undefined until the first fetch
 * lands; callers fall back to the raw role name, and re-render once a
 * subscribed component (RoleChip) is notified.
 */
export function roleMeta(name: string): RoleMeta | undefined {
  return (cache ?? []).find((r) => r.name === name);
}

export function useRoles(): UseRolesResult {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick((t) => t + 1);
    listeners.add(listener);
    void ensureLoaded();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const allRoles = cache ?? [];
  return {
    roles: allRoles.filter((r) => r.retired_at === null),
    allRoles,
    loading: cache === null,
    error: cacheError,
  };
}
